import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import URLInput from './URLInput';
import OutputEditor from './OutputEditor';
import ActionButtons from './ActionButtons';
import SaveRedirectModal from './SaveRedirectModal';
import SavedRedirectsModal from './SavedRedirectsModal';
import { encryptCode } from '../utils/crypto';
import { getSettings } from '../utils/storage';
import supabase from '../supabase/supabase';

const { FiLogOut, FiUser, FiArrowLeft, FiSave, FiFolder } = FiIcons;

const STORAGE_KEY = 'sneaky_redirects_dashboard_state';
const SCROLL_KEY = 'sneaky_redirects_scroll_position';

const Dashboard = ({ user, onLogout, showToast, onBackToAdmin }) => {
  const [url, setUrl] = useState('');
  const [plaintext, setPlaintext] = useState('');
  const [encrypted, setEncrypted] = useState('');
  const [settings] = useState(getSettings());
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showSavedModal, setShowSavedModal] = useState(false);
  const [currentRedirectId, setCurrentRedirectId] = useState(null);
  const [savedRedirectUrl, setSavedRedirectUrl] = useState('');
  const [isLoadedRedirect, setIsLoadedRedirect] = useState(false);

  // Load saved state on mount
  useEffect(() => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        const { url: savedUrl, encrypted: savedEncrypted, redirectId, savedUrl: originalUrl, isLoaded } = JSON.parse(savedState);
        if (savedUrl) setUrl(savedUrl);
        if (savedEncrypted) setEncrypted(savedEncrypted);
        if (redirectId) setCurrentRedirectId(redirectId);
        if (originalUrl) setSavedRedirectUrl(originalUrl);
        if (isLoaded) setIsLoadedRedirect(isLoaded);
      }
    } catch (error) {
      console.error('Error loading saved state:', error);
    }
  }, []);

  // Restore scroll position on mount
  useEffect(() => {
    try {
      const savedScroll = localStorage.getItem(SCROLL_KEY);
      if (savedScroll) {
        const scrollPos = JSON.parse(savedScroll);
        setTimeout(() => {
          window.scrollTo({
            top: scrollPos.y,
            left: scrollPos.x,
            behavior: 'instant'
          });
        }, 0);
      }
    } catch (error) {
      console.error('Error restoring scroll position:', error);
    }
  }, []);

  // Save scroll position on scroll and before unload
  useEffect(() => {
    const saveScrollPosition = () => {
      try {
        const scrollPos = {
          x: window.scrollX,
          y: window.scrollY,
          timestamp: Date.now()
        };
        localStorage.setItem(SCROLL_KEY, JSON.stringify(scrollPos));
      } catch (error) {
        console.error('Error saving scroll position:', error);
      }
    };

    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(saveScrollPosition, 100);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        saveScrollPosition();
      }
    };

    const handleBeforeUnload = () => {
      saveScrollPosition();
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearTimeout(scrollTimeout);
    };
  }, []);

  // Save state whenever url or encrypted changes
  useEffect(() => {
    try {
      const stateToSave = {
        url,
        encrypted,
        redirectId: currentRedirectId,
        savedUrl: savedRedirectUrl,
        isLoaded: isLoadedRedirect,
        timestamp: Date.now()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      console.error('Error saving state:', error);
    }
  }, [url, encrypted, currentRedirectId, savedRedirectUrl, isLoadedRedirect]);

  // Check if URL has changed from the saved redirect - if so, reset the redirect ID
  useEffect(() => {
    if (currentRedirectId && savedRedirectUrl && url !== savedRedirectUrl) {
      // URL has changed, this is a new redirect
      setCurrentRedirectId(null);
      setSavedRedirectUrl('');
      setIsLoadedRedirect(false);
    }
  }, [url, currentRedirectId, savedRedirectUrl]);

  const generateRedirectCode = (inputUrl) => {
    if (!inputUrl.trim()) return '';
    
    const template = settings.redirectTemplate;
    const base64Url = btoa(inputUrl);
    
    return template
      .replace(/URL_ORIGINAL/g, inputUrl)
      .replace(/URL_BASE64/g, base64Url);
  };

  useEffect(() => {
    if (url) {
      const redirectCode = generateRedirectCode(url);
      setPlaintext(redirectCode);
    } else {
      setPlaintext('');
    }
  }, [url, settings.redirectTemplate]);

  const handleEncrypt = async () => {
    if (!url.trim()) {
      showToast('Please enter a URL first', 'error');
      return;
    }

    try {
      const result = encryptCode(plaintext, settings.outputTemplate);
      setEncrypted(result);
      setIsLoadedRedirect(false); // Mark as newly encrypted
      
      await navigator.clipboard.writeText(result);
      showToast('Encrypted code copied to clipboard!');
    } catch (error) {
      console.error(error);
      showToast('Encryption failed: ' + error.message, 'error');
    }
  };

  const handleCopyExisting = async () => {
    if (!encrypted.trim()) {
      showToast('No code to copy', 'error');
      return;
    }

    try {
      await navigator.clipboard.writeText(encrypted);
      showToast('Code copied to clipboard!', 'success');
    } catch (error) {
      console.error(error);
      showToast('Failed to copy code', 'error');
    }
  };

  const handleClearAll = () => {
    setUrl('');
    setEncrypted('');
    setPlaintext('');
    setCurrentRedirectId(null);
    setSavedRedirectUrl('');
    setIsLoadedRedirect(false);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(SCROLL_KEY);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    showToast('Cleared all data', 'success');
  };

  const handleSaveRedirect = async (name) => {
    if (!url.trim() || !encrypted.trim()) {
      showToast('Please generate an encrypted redirect first', 'error');
      return;
    }

    try {
      if (currentRedirectId) {
        // Update existing redirect
        const { error } = await supabase
          .from('saved_redirects')
          .update({
            name: name,
            target_url: url,
            encrypted_code: encrypted
          })
          .eq('id', currentRedirectId);

        if (error) throw error;
        setSavedRedirectUrl(url);
        setIsLoadedRedirect(true);
        showToast('Redirect updated successfully!', 'success');
      } else {
        // Create new redirect
        const { data, error } = await supabase
          .from('saved_redirects')
          .insert({
            user_id: user.id,
            name: name,
            target_url: url,
            encrypted_code: encrypted
          })
          .select()
          .single();

        if (error) throw error;
        setCurrentRedirectId(data.id);
        setSavedRedirectUrl(url);
        setIsLoadedRedirect(true);
        showToast('Redirect saved successfully!', 'success');
      }
    } catch (error) {
      console.error('Error saving redirect:', error);
      showToast('Failed to save redirect', 'error');
      throw error;
    }
  };

  const handleLoadRedirect = (redirect) => {
    setUrl(redirect.target_url);
    setEncrypted(redirect.encrypted_code);
    setCurrentRedirectId(redirect.id);
    setSavedRedirectUrl(redirect.target_url);
    setIsLoadedRedirect(true);
    showToast('Redirect loaded successfully!', 'success');
  };

  return (
    <div className="min-h-screen">
      <div className="bg-slate-800/30 border-b border-slate-700/30 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 max-w-3xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600/20 p-2 rounded-lg">
                <SafeIcon icon={FiUser} className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-slate-200 font-medium">
                  {user.first_name} {user.last_name}
                </p>
                <p className="text-slate-400 text-sm">{user.email}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {onBackToAdmin && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onBackToAdmin}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 rounded-lg transition-all duration-200 border border-purple-500/30"
                >
                  <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
                  <span>Back to Admin</span>
                </motion.button>
              )}
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-slate-700/50 hover:bg-slate-700/70 text-slate-200 rounded-lg transition-all duration-200 border border-slate-600/50"
              >
                <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                <span>Logout</span>
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-8"
        >
          <div className="text-center">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 bg-clip-text text-transparent mb-2">
              Sneaky Redirects
            </h1>
            <p className="text-slate-400 text-lg">
              Generate stealthy, encrypted JavaScript redirects instantly
            </p>
          </div>

          {/* Action Bar */}
          <div className="flex gap-3 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowSavedModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 rounded-xl transition-all duration-200 border border-purple-500/30"
            >
              <SafeIcon icon={FiFolder} className="w-5 h-5" />
              <span className="font-medium">My Redirects</span>
            </motion.button>

            {encrypted && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowSaveModal(true)}
                className="flex items-center gap-2 px-6 py-3 bg-green-600/20 hover:bg-green-600/30 text-green-400 rounded-xl transition-all duration-200 border border-green-500/30"
              >
                <SafeIcon icon={FiSave} className="w-5 h-5" />
                <span className="font-medium">
                  {currentRedirectId ? 'Update' : 'Save'}
                </span>
              </motion.button>
            )}
          </div>
          
          <div className="space-y-6 bg-slate-800/30 p-6 rounded-2xl border border-slate-700/30 backdrop-blur-sm">
            <URLInput value={url} onChange={setUrl} />
            
            <ActionButtons 
              onEncrypt={handleEncrypt} 
              onClear={handleClearAll}
              onCopyExisting={isLoadedRedirect && encrypted ? handleCopyExisting : null}
              isLoadedRedirect={isLoadedRedirect}
            />
            
            <OutputEditor value={encrypted} onChange={setEncrypted} />
          </div>
        </motion.div>
      </div>

      {/* Modals */}
      <SaveRedirectModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveRedirect}
        showToast={showToast}
        isUpdate={!!currentRedirectId}
      />

      <SavedRedirectsModal
        isOpen={showSavedModal}
        onClose={() => setShowSavedModal(false)}
        onLoad={handleLoadRedirect}
        showToast={showToast}
        userId={user.id}
      />
    </div>
  );
};

export default Dashboard;